import { Injectable } from '@angular/core';

/**
 * Client-side API Rate Limiter
 * 
 * Prevents excessive API calls from the frontend (e.g., spam-clicking buttons).
 * Implements per-endpoint throttling with configurable windows.
 *
 * This reduces server load and prevents accidental duplicate submissions.
 */
@Injectable({
  providedIn: 'root'
})
export class ApiRateLimiterService {

  /** Default: max 10 requests per 10 seconds per endpoint */
  private readonly DEFAULT_MAX_REQUESTS = 10;
  private readonly DEFAULT_WINDOW_MS = 10_000;

  /** Stricter limits for sensitive endpoints */
  private readonly ENDPOINT_LIMITS: Record<string, { max: number; windowMs: number }> = {
    '/auth/login': { max: 3, windowMs: 60_000 },        // 3 per minute
    '/auth/signup': { max: 2, windowMs: 60_000 },       // 2 per minute
    '/auth/forgot': { max: 2, windowMs: 120_000 },      // 2 per 2 minutes
    '/payments/ccavenue/initiate': { max: 3, windowMs: 60_000 }, // 3 per minute
    '/super-admin/approve': { max: 10, windowMs: 30_000 },       // 10 per 30s
    '/super-admin/reject': { max: 10, windowMs: 30_000 },
  };

  /** Track request timestamps per endpoint pattern */
  private requestLog: Map<string, number[]> = new Map();

  /**
   * Check if a request to the given URL is allowed under rate limits.
   * Returns true if allowed, false if rate limited.
   */
  isAllowed(url: string): boolean {
    const endpoint = this.extractEndpoint(url);
    const limits = this.getLimitsForEndpoint(endpoint);
    const now = Date.now();

    // Get or create the request log for this endpoint
    let timestamps = this.requestLog.get(endpoint) || [];

    // Remove expired entries outside the window
    timestamps = timestamps.filter(t => now - t < limits.windowMs);

    // Check if we've exceeded the limit
    if (timestamps.length >= limits.max) {
      return false;
    }

    // Record this request
    timestamps.push(now);
    this.requestLog.set(endpoint, timestamps);

    return true;
  }

  /**
   * Get remaining requests allowed for an endpoint.
   */
  getRemainingRequests(url: string): number {
    const endpoint = this.extractEndpoint(url);
    const limits = this.getLimitsForEndpoint(endpoint);
    const now = Date.now();

    const timestamps = (this.requestLog.get(endpoint) || [])
      .filter(t => now - t < limits.windowMs);

    return Math.max(0, limits.max - timestamps.length);
  }

  /**
   * Get seconds until next request is allowed (0 if not rate limited).
   */
  getRetryAfterSeconds(url: string): number {
    const endpoint = this.extractEndpoint(url);
    const limits = this.getLimitsForEndpoint(endpoint);
    const now = Date.now();

    const timestamps = (this.requestLog.get(endpoint) || [])
      .filter(t => now - t < limits.windowMs);

    if (timestamps.length < limits.max) return 0;

    // Find the oldest timestamp in the window — that's when the first slot frees up
    const oldest = Math.min(...timestamps);
    const freeAt = oldest + limits.windowMs;
    return Math.max(0, Math.ceil((freeAt - now) / 1000));
  }

  /**
   * Reset rate limit tracking for a specific endpoint (e.g., after successful login).
   */
  reset(url: string): void {
    const endpoint = this.extractEndpoint(url);
    this.requestLog.delete(endpoint);
  }

  /**
   * Clear all rate limit tracking.
   */
  resetAll(): void {
    this.requestLog.clear();
  }

  private extractEndpoint(url: string): string {
    try {
      // Extract path after /api/
      const match = url.match(/\/api(\/[^\?#]*)/);
      if (match) return match[1];
    } catch { /* fallback */ }
    return url;
  }

  private getLimitsForEndpoint(endpoint: string): { max: number; windowMs: number } {
    // Check for exact match first
    if (this.ENDPOINT_LIMITS[endpoint]) {
      return this.ENDPOINT_LIMITS[endpoint];
    }

    // Check for prefix match (e.g., /super-admin/approve-guide/123 matches /super-admin/approve)
    for (const [pattern, limits] of Object.entries(this.ENDPOINT_LIMITS)) {
      if (endpoint.startsWith(pattern)) {
        return limits;
      }
    }

    return { max: this.DEFAULT_MAX_REQUESTS, windowMs: this.DEFAULT_WINDOW_MS };
  }
}
